import { useLayoutEffect, useRef, useState } from "react"

export type Animation = {
  repeatCount: "indefinite" | number
  dur: number // milliseconds
  keyTimes: number[]
  attribute: string
  values: string[]
}

// If you are using this, make sure onIdle is memoized
export const useAnimateSvg = (animations: Animation[], onIdle?: () => void) => {
  const [activeIndexes, setActiveIndexes] = useState<number[]>(animations.map(() => 0))

  useLayoutEffect(() => {
    let start: number | null = null
    let loop: number | null = null
    let allIdle = true
    function update(time: number) {
      if (start === null) {
        start = time
      }

      const elapsed = time - start
      const newIndexes = animations.map((animation) => {
        const { dur, keyTimes, repeatCount } = animation
        const loopCount = Math.floor(elapsed / dur)
        if (repeatCount !== "indefinite" && loopCount > repeatCount) {
          return 0
        }
        allIdle = false

        const t = elapsed % dur
        const index = keyTimes.findIndex((keyTime, keyIdx) => {
          if (keyIdx === 0) {
            return t >= 0 && t < keyTimes[1]
          }
          return t < keyTime
        })
        const activeIndex = index === -1 ? 0 : index
        return activeIndex
      })

      setActiveIndexes(newIndexes)

      if (allIdle) {
        if (onIdle) onIdle()
        return
      }

      loop = requestAnimationFrame(update)
    }

    loop = requestAnimationFrame(update)

    return () => {
      if (loop) {
        cancelAnimationFrame(loop)
      }
    }
  }, [animations, onIdle])

  return animations.map((animation, i) => {
    const { attribute, values } = animation
    const value = values[activeIndexes[i]]
    return { [attribute]: value }
  })
}
